// Firestore rejects from inside its own network callbacks, so its errors carry no app frames and
// Sentry can't show which query failed. This wraps the compat read/write methods to capture the
// caller's stack up front and, on failure only, attach it as `error.cause` (Sentry's linked errors
// render it as a chained exception) plus the op, collection path (document IDs stripped) and
// whether the user was signed in. Never includes filter values, document IDs or user data.

const PATCHED_FLAG = '__fpCallSitePatched'

// `customers/abc/subscriptions/xyz` -> `customers/{id}/subscriptions/{id}` - keeps IDs out of tags and grouping
const normalizePath = (path) => {
  if (typeof path !== 'string') return undefined
  return path.split('/').map((segment, index) => index % 2 === 1 ? '{id}' : segment).join('/')
}

const describeTarget = (firebase, target) => {
  try {
    if (target instanceof firebase.firestore.DocumentReference) {
      return { kind: 'doc', path: normalizePath(target.path) }
    }

    const query = target?._delegate?._query
    return { kind: 'query', path: normalizePath(query?.path?.canonicalString?.()) }
  } catch (error) {
    return {}
  }
}

const isSignedIn = (firebase) => {
  try {
    return Boolean(firebase.auth?.()?.currentUser)
  } catch (error) {
    return undefined
  }
}

const annotateError = (firebase, error, target, op, callSite) => {
  try {
    if (!error || typeof error !== 'object') return

    const context = { op, ...describeTarget(firebase, target), signedIn: isSignedIn(firebase) }

    Object.defineProperty(error, 'firestoreContext', {
      value: context,
      configurable: true,
      writable: true,
      enumerable: false
    })

    if (error.cause === undefined) {
      // e.g. "Firestore query.get on invitations"
      callSite.message = `Firestore ${context.kind || 'unknown'}.${op} on ${context.path || 'unknown'}`
      Object.defineProperty(error, 'cause', {
        value: callSite,
        configurable: true,
        writable: true,
        enumerable: false
      })
    }
  } catch (annotationError) {
    // Never let diagnostics change the failure the caller sees
  }
}

const wrapMethod = (firebase, proto, op) => {
  const original = proto?.[op]
  if (typeof original !== 'function') return

  proto[op] = function (...args) {
    const callSite = new Error('Firestore call site')
    const result = original.apply(this, args)

    // Only decorate promise rejections - sync throws and non-promise returns pass through untouched
    if (!result || typeof result.then !== 'function') return result

    const target = this
    return result.then(undefined, error => {
      annotateError(firebase, error, target, op, callSite)
      throw error
    })
  }
}

export const instrumentFirestoreCallSites = (firebase) => {
  try {
    const { Query, DocumentReference } = firebase?.firestore || {}
    if (!Query || !DocumentReference) return
    if (Query.prototype[PATCHED_FLAG]) return

    wrapMethod(firebase, Query.prototype, 'get')
    wrapMethod(firebase, DocumentReference.prototype, 'get')
    wrapMethod(firebase, DocumentReference.prototype, 'set')
    wrapMethod(firebase, DocumentReference.prototype, 'update')
    wrapMethod(firebase, DocumentReference.prototype, 'delete')

    Object.defineProperty(Query.prototype, PATCHED_FLAG, { value: true })
  } catch (error) {
    console.error('Error instrumenting Firestore call sites', error)
  }
}

// Sentry beforeSend helper - copies the attached context onto the event as searchable tags
export const applyFirestoreContextToEvent = (event, hint) => {
  const context = hint?.originalException?.firestoreContext
  if (!context) return

  event.tags = {
    ...event.tags,
    'firestore.op': context.op,
    'firestore.kind': context.kind || 'unknown',
    'firestore.path': context.path || 'unknown',
    'firestore.signed_in': String(context.signedIn)
  }
  event.contexts = {
    ...event.contexts,
    firestore: {
      op: context.op,
      kind: context.kind,
      path: context.path,
      signedIn: context.signedIn
    }
  }
}
